import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { providerAPI } from "../../services/api";
import { useProvider } from "../../hooks/useProviders";

// Which truckload modes a carrier handles. Decides whether an FTL or an LTL
// quote request reaches them at all, so trucking carriers must pick one.
const TRUCKING_MODE_OPTIONS = [
	{ value: "FTL", label: "FTL only (full truckload)" },
	{ value: "LTL", label: "LTL only (less-than-truckload)" },
	{ value: "both", label: "Both FTL and LTL" },
];

const handlesTrucking = (serviceType) =>
	serviceType === "domestic_trucking" || serviceType === "both";

const ProviderForm = ({ isEdit = false }) => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { provider, loading } = isEdit
		? useProvider(id)
		: { provider: null, loading: false };

	const [formData, setFormData] = useState({
		name: "",
		type: "FORWARDER",
		status: "ACTIVE",
		serviceType: "ocean_freight",
		truckingMode: "",
		contactEmail: "",
		contactPhone: "",
		contactName: "",
		preferredChannel: "email",
		phoneNumber: "",
		whatsappNumber: "",
		notes: "",
		primaryServiceLanes: [],
		containerTypes: [],
		tags: [],
	});

	const [newLane, setNewLane] = useState({
		originPort: "",
		destinationPort: "",
	});
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (provider) {
			setFormData({
				name: provider.name || "",
				type: provider.type || "FORWARDER",
				status: provider.status || "ACTIVE",
				serviceType: provider.serviceType || "ocean_freight",
				truckingMode: provider.truckingMode || "",
				contactEmail: provider.contactEmail || "",
				contactPhone: provider.contactPhone || "",
				contactName: provider.contactName || "",
				preferredChannel: provider.preferredChannel || "email",
				phoneNumber: provider.phoneNumber || "",
				whatsappNumber: provider.whatsappNumber || "",
				notes: provider.notes || "",
				primaryServiceLanes: provider.primaryServiceLanes || [],
				containerTypes: provider.containerTypes || [],
				tags: provider.tags || [],
			});
		}
	}, [provider]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleContainerTypeToggle = (type) => {
		setFormData((prev) => ({
			...prev,
			containerTypes: prev.containerTypes.includes(type)
				? prev.containerTypes.filter((t) => t !== type)
				: [...prev.containerTypes, type],
		}));
	};

	const handleAddLane = () => {
		if (newLane.originPort && newLane.destinationPort) {
			setFormData((prev) => ({
				...prev,
				primaryServiceLanes: [...prev.primaryServiceLanes, { ...newLane }],
			}));
			setNewLane({ originPort: "", destinationPort: "" });
		}
	};

	const handleRemoveLane = (index) => {
		setFormData((prev) => ({
			...prev,
			primaryServiceLanes: prev.primaryServiceLanes.filter(
				(_, i) => i !== index,
			),
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		setError(null);

		try {
			// An ocean-only provider has no truckload mode, and "" is not a legal
			// value for the column — send null so it clears instead of failing.
			const payload = {
				...formData,
				truckingMode: handlesTrucking(formData.serviceType)
					? formData.truckingMode
					: null,
			};

			if (isEdit) {
				await providerAPI.update(id, payload);
			} else {
				await providerAPI.create(payload);
			}
			navigate("/providers");
		} catch (err) {
			setError(err.response?.data?.message || "Failed to save provider");
			setSubmitting(false);
		}
	};

	if (isEdit && loading) {
		return <div className="loading">Loading provider...</div>;
	}

	return (
		<div className="provider-form">
			<div className="page-header">
				<h1>{isEdit ? "Edit Provider" : "Add New Provider"}</h1>
			</div>

			{error && <div className="error-message">{error}</div>}

			<form onSubmit={handleSubmit}>
				<div className="form-section">
					<h2>Basic Information</h2>

					<div className="form-group">
						<label htmlFor="name">Provider Name *</label>
						<input
							type="text"
							id="name"
							name="name"
							value={formData.name}
							onChange={handleChange}
							required
							maxLength={100}
						/>
					</div>

					<div className="form-row">
						<div className="form-group">
							<label htmlFor="type">Type *</label>
							<select
								id="type"
								name="type"
								value={formData.type}
								onChange={handleChange}
								required
							>
								<option value="FORWARDER">Forwarder</option>
								<option value="CARRIER">Carrier</option>
								<option value="BROKER">Broker</option>
							</select>
						</div>

						<div className="form-group">
							<label htmlFor="status">Status *</label>
							<select
								id="status"
								name="status"
								value={formData.status}
								onChange={handleChange}
							>
								<option value="ACTIVE">Active</option>
								<option value="INACTIVE">Inactive</option>
								<option value="BLACKLISTED">Blacklisted</option>
							</select>
						</div>
					</div>
				</div>

				<div className="form-section">
					<h2>Contact Information</h2>

					<div className="form-group">
						<label htmlFor="contactEmail">Email *</label>
						<input
							type="email"
							id="contactEmail"
							name="contactEmail"
							value={formData.contactEmail}
							onChange={handleChange}
							required
						/>
					</div>

					<div className="form-row">
						<div className="form-group">
							<label htmlFor="contactName">Contact Name</label>
							<input
								type="text"
								id="contactName"
								name="contactName"
								value={formData.contactName}
								onChange={handleChange}
							/>
						</div>

						<div className="form-group">
							<label htmlFor="contactPhone">Phone</label>
							<input
								type="tel"
								id="contactPhone"
								name="contactPhone"
								value={formData.contactPhone}
								onChange={handleChange}
							/>
						</div>
					</div>

					<div className="form-row">
						<div className="form-group">
							<label htmlFor="preferredChannel">Preferred Channel</label>
							<select
								id="preferredChannel"
								name="preferredChannel"
								value={formData.preferredChannel}
								onChange={handleChange}
							>
								<option value="email">Email only</option>
								{/* <option value="sms">Email + SMS nudge</option>
                <option value="whatsapp">Email + WhatsApp nudge</option> */}
							</select>
						</div>

						<div className="form-group">
							<label htmlFor="phoneNumber">
								SMS Number {formData.preferredChannel === "sms" && "*"}
							</label>
							<input
								type="tel"
								id="phoneNumber"
								name="phoneNumber"
								value={formData.phoneNumber}
								onChange={handleChange}
								placeholder="+521234567890"
								required={formData.preferredChannel === "sms"}
							/>
							<small className="form-hint">
								E.164 format: + country code + number
							</small>
						</div>
					</div>
				</div>

				<div className="form-section">
					<h2>Service Coverage</h2>

					<div className="form-row">
						<div className="form-group">
							<label htmlFor="serviceType">Service Type *</label>
							<select
								id="serviceType"
								name="serviceType"
								value={formData.serviceType}
								onChange={handleChange}
								required
							>
								<option value="ocean_freight">International Ocean Freight</option>
								<option value="domestic_trucking">Domestic Trucking</option>
								<option value="both">Both</option>
							</select>
						</div>

						{handlesTrucking(formData.serviceType) && (
							<div className="form-group">
								<label htmlFor="truckingMode">Truckload Mode *</label>
								<select
									id="truckingMode"
									name="truckingMode"
									value={formData.truckingMode}
									onChange={handleChange}
									required
								>
									<option value="" disabled>
										Select truckload mode
									</option>
									{TRUCKING_MODE_OPTIONS.map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</select>
								<small>
									Only quote requests for the modes selected here reach this
									carrier.
								</small>
							</div>
						)}
					</div>

					<div className="form-group">
						<label>Container Types</label>
						<div className="checkbox-group">
							{["20GP", "40GP", "40HC", "45HC"].map((type) => (
								<label key={type} className="checkbox-label">
									<input
										type="checkbox"
										checked={formData.containerTypes.includes(type)}
										onChange={() => handleContainerTypeToggle(type)}
									/>
									{type}
								</label>
							))}
						</div>
					</div>

					<div className="form-group">
						<label>Service Lanes</label>
						<div className="lane-input">
							<input
								type="text"
								placeholder="Origin Port (e.g., CNSHA)"
								value={newLane.originPort}
								onChange={(e) =>
									setNewLane((prev) => ({
										...prev,
										originPort: e.target.value.toUpperCase(),
									}))
								}
								maxLength={5}
							/>
							<span>→</span>
							<input
								type="text"
								placeholder="Destination Port (e.g., USLAX)"
								value={newLane.destinationPort}
								onChange={(e) =>
									setNewLane((prev) => ({
										...prev,
										destinationPort: e.target.value.toUpperCase(),
									}))
								}
								maxLength={5}
							/>
							<button
								type="button"
								onClick={handleAddLane}
								className="btn btn-sm btn-secondary"
							>
								Add Lane
							</button>
						</div>
						<div className="lane-list">
							{formData.primaryServiceLanes.map((lane, index) => (
								<div key={index} className="lane-item">
									<span>
										{lane.originPort} → {lane.destinationPort}
									</span>
									<button
										type="button"
										onClick={() => handleRemoveLane(index)}
										className="btn-remove"
									>
										×
									</button>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="form-section">
					<h2>Notes</h2>
					<div className="form-group">
						<textarea
							id="notes"
							name="notes"
							value={formData.notes}
							onChange={handleChange}
							rows={4}
							placeholder="Any additional notes about this provider..."
						/>
					</div>
				</div>

				<div className="form-actions">
					<button
						type="button"
						onClick={() => navigate("/providers")}
						className="btn btn-secondary"
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={submitting}
					>
						{submitting
							? "Saving..."
							: isEdit
								? "Update Provider"
								: "Create Provider"}
					</button>
				</div>
			</form>
		</div>
	);
};

export default ProviderForm;
